import type { HREmployee, HREmployeeLifecycle } from '@cloud/db'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Check, GitBranch, X } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

// ── Types ─────────────────────────────────────────────────────────────────────

type LifecycleType = 'promotion' | 'transfer' | 'confirmation' | 'separation' | 'salary_revision'
type LifecycleStatus = 'pending' | 'approved' | 'rejected' | 'completed'

const LIFECYCLE_TYPES: { value: LifecycleType; label: string }[] = [
  { value: 'promotion', label: 'Promotion' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'confirmation', label: 'Confirmation' },
  { value: 'separation', label: 'Separation' },
  { value: 'salary_revision', label: 'Salary Revision' },
]

const lifecycleTypeColors: Record<LifecycleType, string> = {
  promotion: 'bg-green-100 text-green-800',
  transfer: 'bg-blue-100 text-blue-800',
  confirmation: 'bg-teal-100 text-teal-800',
  separation: 'bg-red-100 text-red-800',
  salary_revision: 'bg-purple-100 text-purple-800',
}

// ── Schemas ───────────────────────────────────────────────────────────────────

const newEventSchema = z.object({
  employeeId: z.number().min(1, 'Employee is required'),
  type: z.enum(['promotion', 'transfer', 'confirmation', 'separation', 'salary_revision']),
  effectiveDate: z.string().min(1, 'Effective date is required'),
  reason: z.string().optional(),
  // promotion fields
  newJobTitle: z.string().optional(),
  newSalary: z.string().optional(),
  // transfer fields
  newDepartmentId: z.number().optional(),
  // separation fields
  finalWorkingDay: z.string().optional(),
  relievingDate: z.string().optional(),
  // confirmation fields
  confirmationDate: z.string().optional(),
})

const rejectEventSchema = z.object({
  reason: z.string().min(1, 'Reason is required'),
})

type NewEventFormValues = z.infer<typeof newEventSchema>
type RejectEventFormValues = z.infer<typeof rejectEventSchema>

export const Route = createFileRoute('/hr/lifecycle/')({
  component: LifecyclePage,
})

function LifecyclePage() {
  return (
    <ModuleGuard moduleKey="hr">
      <LifecycleContent />
    </ModuleGuard>
  )
}

function LifecycleContent() {
  const [employeeFilter, setEmployeeFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  })

  const queryClient = useQueryClient()

  const { data: eventsData, isLoading } = useQuery(
    orpc.hr.listLifecycleEvents.queryOptions({
      input: {
        employeeId: employeeFilter !== 'all' ? Number(employeeFilter) : undefined,
        type: typeFilter !== 'all' ? (typeFilter as LifecycleType) : undefined,
        status: statusFilter !== 'all' ? (statusFilter as LifecycleStatus) : undefined,
        page: 1,
        limit: 100,
      },
    }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const eventsPage = eventsData as { data?: HREmployeeLifecycle[]; total?: number } | null | undefined
  const events = (eventsPage?.data ?? []) as HREmployeeLifecycle[]
  const employees = (employeesData?.data ?? []) as HREmployee[]

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: [['hr', 'listLifecycleEvents']] })
  }

  const createMutation = useMutation(
    orpc.hr.createLifecycleEvent.mutationOptions({
      onSuccess: () => {
        toast.success('Event created')
        refetch()
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const approveMutation = useMutation(
    orpc.hr.approveLifecycleEvent.mutationOptions({
      onSuccess: () => {
        toast.success('Event approved')
        refetch()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const rejectMutation = useMutation(
    orpc.hr.rejectLifecycleEvent.mutationOptions({
      onSuccess: () => {
        toast.success('Event rejected')
        refetch()
        setRejectDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const completeMutation = useMutation(
    orpc.hr.completeLifecycleEvent.mutationOptions({
      onSuccess: () => {
        toast.success('Event completed')
        refetch()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const getEmployeeName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  const formatNewValues = (event: HREmployeeLifecycle) => {
    const nv = event.newValues as Record<string, unknown>
    const parts: string[] = []
    if (nv.jobTitle) parts.push(`Title: ${nv.jobTitle}`)
    if (nv.salary) parts.push(`Salary: ${nv.salary}`)
    if (nv.departmentId) parts.push(`Dept ID: ${nv.departmentId}`)
    if (nv.confirmationDate) parts.push(`Confirmation: ${nv.confirmationDate}`)
    if (nv.finalWorkingDay) parts.push(`Last Day: ${nv.finalWorkingDay}`)
    if (nv.relievingDate) parts.push(`Relieving: ${nv.relievingDate}`)
    return parts.join(' | ') || '-'
  }

  const handleCreateEvent = async (data: NewEventFormValues) => {
    const newValues: Record<string, unknown> = {}

    if (data.type === 'promotion') {
      if (data.newJobTitle) newValues.jobTitle = data.newJobTitle
      if (data.newSalary) newValues.salary = data.newSalary
    } else if (data.type === 'transfer') {
      if (data.newDepartmentId) newValues.departmentId = data.newDepartmentId
    } else if (data.type === 'salary_revision') {
      if (data.newSalary) newValues.salary = data.newSalary
    } else if (data.type === 'separation') {
      if (data.finalWorkingDay) newValues.finalWorkingDay = data.finalWorkingDay
      if (data.relievingDate) newValues.relievingDate = data.relievingDate
    } else if (data.type === 'confirmation') {
      if (data.confirmationDate) newValues.confirmationDate = data.confirmationDate
    }

    await createMutation.mutateAsync({
      employeeId: data.employeeId,
      type: data.type,
      effectiveDate: data.effectiveDate,
      reason: data.reason,
      newValues,
    })
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Lifecycle Events</h1>
          <p className="text-muted-foreground">Track employee lifecycle changes</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <GitBranch className="h-4 w-4 mr-2" />
          New Event
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id.toString()}>
                    {emp.firstName} {emp.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {LIFECYCLE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <GitBranch className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No lifecycle events found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Effective Date</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Changes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-medium">
                      {getEmployeeName(event.employeeId)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`${lifecycleTypeColors[event.type as LifecycleType] ?? ''} capitalize`}
                        variant="outline"
                      >
                        {event.type.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {event.effectiveDate
                        ? new Date(event.effectiveDate).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {event.reason ?? '-'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                      {formatNewValues(event)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={event.status ?? 'pending'} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {event.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-600"
                              onClick={() => approveMutation.mutate({ id: event.id })}
                              disabled={approveMutation.isPending}
                              title="Approve"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => setRejectDialog({ open: true, id: event.id })}
                              title="Reject"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {event.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => completeMutation.mutate({ id: event.id })}
                            disabled={completeMutation.isPending}
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewEventDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        employees={employees}
        onSubmit={handleCreateEvent}
      />

      <RejectEventDialog
        open={rejectDialog.open}
        onOpenChange={(open) => setRejectDialog({ open, id: null })}
        isPending={rejectMutation.isPending}
        onSubmit={(reason) => {
          if (rejectDialog.id) {
            rejectMutation.mutate({ id: rejectDialog.id, reason })
          }
        }}
      />
    </div>
  )
}

interface NewEventDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: HREmployee[]
  onSubmit: (data: NewEventFormValues) => Promise<void>
}

function NewEventDialog({ open, onOpenChange, employees, onSubmit }: NewEventDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<NewEventFormValues>({
    resolver: zodResolver(newEventSchema),
    defaultValues: {
      effectiveDate: '',
      reason: '',
      newJobTitle: '',
      newSalary: '',
      finalWorkingDay: '',
      relievingDate: '',
      confirmationDate: '',
    },
  })

  const selectedType = form.watch('type')

  const handleSubmit = async (data: NewEventFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Lifecycle Event</DialogTitle>
          <DialogDescription>Record an employee lifecycle change</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Event Type *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v as LifecycleType)}
                    value={field.value ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LIFECYCLE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="effectiveDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Effective Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Reason for the event..." rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Promotion fields */}
            {selectedType === 'promotion' && (
              <>
                <FormField
                  control={form.control}
                  name="newJobTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Job Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Senior Engineer" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="newSalary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Salary</FormLabel>
                      <FormControl>
                        <Input placeholder="75000" type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* Transfer fields */}
            {selectedType === 'transfer' && (
              <FormField
                control={form.control}
                name="newDepartmentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Department</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Department ID"
                        type="number"
                        value={field.value?.toString() ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : undefined)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Salary revision fields */}
            {selectedType === 'salary_revision' && (
              <FormField
                control={form.control}
                name="newSalary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Salary</FormLabel>
                    <FormControl>
                      <Input placeholder="75000" type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Separation fields */}
            {selectedType === 'separation' && (
              <>
                <FormField
                  control={form.control}
                  name="finalWorkingDay"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Final Working Day</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="relievingDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Relieving Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* Confirmation fields */}
            {selectedType === 'confirmation' && (
              <FormField
                control={form.control}
                name="confirmationDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmation Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Event'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface RejectEventDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isPending: boolean
  onSubmit: (reason: string) => void
}

function RejectEventDialog({ open, onOpenChange, isPending, onSubmit }: RejectEventDialogProps) {
  const form = useForm<RejectEventFormValues>({
    resolver: zodResolver(rejectEventSchema),
    defaultValues: { reason: '' },
  })

  const handleSubmit = (data: RejectEventFormValues) => {
    onSubmit(data.reason)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Reject Event</DialogTitle>
          <DialogDescription>Provide a reason for rejecting this lifecycle event.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason *</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Reason for rejection..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={isPending}>
                {isPending ? 'Rejecting...' : 'Reject Event'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
